import * as columnNames from "./column-names";

/*


    name-> name of the column as stored in the database

    heading -> heading of the column that will be displayed in the front-end

    contentClasses -> Array. the classes that will get applied to the wrapper div inside the td tag

    width -> the width will be applied to the th, td tag and if applyWidthOnContent is set to true, 
             then same width will be applied to the wrapper div tag

    contentType -> the content type of the column, like String or Number or Data or Icon or Custom

    isRenderCustomComponent -> Boolean. if the content inside the td element is not simply a text,
                                but another component, then this should be set to true

    sortable -> Boolean. if the column is sortable or not and the sort icon will be visible if set to true

    hasToolTipInfo -> Boolean. if true will display the tooltip icon near column heading

    toolTipContent -> This is the content that will get rendered when clicked on tooltip

    headerClasses -> Array. the classes will get applied to the th tag

    showTooltip --> Boolean. if tru shows the tooltip for the column data on hover
*/

class ColumnMetaData {
  constructor(data) {
    const { name, heading, contentClasses, contentType, isRenderCustomComponent, sortable, hasToolTipInfo, toolTipContent, headerClasses, transformContent, style, truncateContent, truncateLineNumber, minWidth, maxWidth, fixedWidth, textAlign, applyWidthOnContent, hasRouterLink, routerName, routerParams, showTooltip, colgroupstyle, sort, tooltipLength } = data;

    this.name = name;
    this.heading = heading;
    this.contentClasses = contentClasses || [];
    this.contentType = contentType || String;
    this.isRenderCustomComponent = isRenderCustomComponent || false;
    this.sortable = sortable || false;
    this.hasToolTipInfo = hasToolTipInfo || false;
    this.showTooltip = showTooltip || false;
    this.tooltipLength = tooltipLength || 20;
    this.toolTipContent = toolTipContent || "";
    this.headerClasses = headerClasses || [];
    this.transformContent = transformContent || false;
    this.style = style || {};
    this.truncateContent = truncateContent || true;
    this.truncateLineNumber = truncateLineNumber || 1;
    this.applyWidthOnContent = applyWidthOnContent || false;
    this.minWidth = minWidth || null;
    this.maxWidth = maxWidth || null;
    this.fixedWidth = fixedWidth || null;
    this.textAlign = textAlign || "left";
    this.hasRouterLink = hasRouterLink || false;
    this.routerName = routerName || "";
    this.routerParams = routerParams || {};
    this.colgroupstyle = colgroupstyle || "";
    this.sort = sort || "ASC";
  }
}

const columns = {
  [columnNames.PIN]: new ColumnMetaData({
    name: columnNames.PIN,
    heading: "Pin",
    contentClasses: ["text-align-center"],
    fixedWidth: "35px",
    contentType: "Icon",
    isRenderCustomComponent: true,
    sortable: true,
    sort: "ASC",
    headerClasses: ["text-align-center"],
    colgroupstyle: "width: 3%"
  }),

  [columnNames.NAME]: new ColumnMetaData({
    name: columnNames.NAME,
    heading: "Name",
    contentClasses: ["name"],
    minWidth: "200px",
    maxWidth: "200px",
    contentType: "String",
    sortable: true,
    sort: "ASC",
    truncateContent: true,
    hasRouterLink: true,
    routerName: "activity-details",
    routerParams: { activityId: "id" },
    showTooltip: true,
    colgroupstyle: "width: 15%"
  }),

  [columnNames.TYPE]: new ColumnMetaData({
    name: columnNames.TYPE,
    heading: "Type",
    contentClasses: ["type"],
    maxWidth: "75px",
    minWidth: "75px",
    contentType: "String",
    sortable: true,
    sort: "ASC",
    showTooltip: true,
    colgroupstyle: "width: 12%"
  }),

  [columnNames.THERAPUETIC_AREA]: new ColumnMetaData({
    name: columnNames.THERAPUETIC_AREA,
    heading: "Therapeutic Area",
    contentClasses: [],
    maxWidth: "150px",
    contentType: "String",
    sortable: true,
    sort: "ASC",
    truncateContent: true,
    showTooltip: true,
    colgroupstyle: "width: 15%"
  }),

  [columnNames.GROUP]: new ColumnMetaData({
    name: columnNames.GROUP,
    heading: "Group",
    contentClasses: [],
    maxWidth: "150px",
    contentType: "String",
    sortable: true,
    sort: "ASC",
    truncateContent: true,
    showTooltip: true,
    tooltipLength: 10,
    colgroupstyle: "width:5%;"
  }),

  [columnNames.STEPS]: new ColumnMetaData({
    name: columnNames.STEPS,
    heading: "Steps",
    contentClasses: ["fs-14"],
    minWidth: "55px",
    fixedWidth: "55px",
    contentType: "Number",
    textAlign: "center",
    sortable: true,
    sort: "ASC",
    headerClasses: ["text-align-center"],
    colgroupstyle: "width:5%;"
  }),

  [columnNames.DURATION]: new ColumnMetaData({
    name: columnNames.DURATION,
    heading: "Duration",
    contentClasses: ["fs-14", "pl-5"],
    minWidth: "65px",
    contentType: "Number",
    hasToolTipInfo: true,
    toolTipContent: "Duration to complete the activity in minutes.",
    sortable: false,
    applyWidth: true,
    colgroupstyle: "width:7%;"
  }),
  [columnNames.LANGUAGE]: new ColumnMetaData({
    name: columnNames.LANGUAGE,
    heading: "Languages",
    contentClasses: ["text-align-center", "fs-14"],
    minWidth: "55px",
    contentType: "Object",
    isRenderCustomComponent: true,
    sortable: false,
    headerClasses: ["text-align-center"],
    colgroupstyle: "width:7%;"
  }),

  [columnNames.LAST_MODIFIED]: new ColumnMetaData({
    name: columnNames.LAST_MODIFIED,
    heading: "Last Modified",
    contentClasses: [],
    maxWidth: "80px",
    fixedWidth: "80px",
    contentType: "Date",
    sortable: true,
    sort: "ASC",
    transformContent: true,
    truncateContent: true,
    colgroupstyle: "width:11%"
  }),

  [columnNames.TAGS]: new ColumnMetaData({
    name: columnNames.TAGS,
    heading: "Tags",
    contentClasses: ["nowrap"],
    minWidth: "120px",
    maxWidth: "180px",
    contentType: "Object",
    sortable: false,
    isRenderCustomComponent: true,
    colgroupstyle: "width: 15%;"
  }),

  [columnNames.STUDIES]: new ColumnMetaData({
    name: columnNames.STUDIES,
    heading: "Studies",
    contentClasses: ["text-align-center", "fs-14"],
    minWidth: "55px",
    contentType: "Object",
    isRenderCustomComponent: true,
    sortable: false,
    headerClasses: ["text-align-center"],
    colgroupstyle: "width:7%;"
  }),

  [columnNames.VERSION]: new ColumnMetaData({
    name: columnNames.VERSION,
    heading: "Version",
    contentClasses: ["text-align-center", "fs-14"],
    minWidth: "55px",
    contentType: "Number",
    isRenderCustomComponent: true,
    sortable: false,
    headerClasses: ["text-align-center"],
    colgroupstyle: "width:7%;"
  }),

  [columnNames.INSTANCES]: new ColumnMetaData({
    name: columnNames.INSTANCES,
    heading: "Instances",
    contentClasses: ["text-align-center", "fs-14"],
    minWidth: "55px",
    contentType: "Object",
    isRenderCustomComponent: true,
    sortable: false,
    headerClasses: ["text-align-center"],
    colgroupstyle: "width:7%;"
  }),

  [columnNames.OBJECTIVE]: new ColumnMetaData({
    name: columnNames.OBJECTIVE,
    heading: "Objective",
    contentClasses: ["fs-11"],
    fixedWidth: "180px",
    contentType: "String",
    sortable: true,
    sort: "ASC",
    truncateContent: true,
    truncateLineNumber: 2,
    applyWidthOnContent: true,
    showTooltip: true,
    colgroupstyle: "width:15%;"
  }),

  [columnNames.ACTION]: new ColumnMetaData({
    name: columnNames.ACTION,
    heading: "",
    contentClasses: ["text-align-right"],
    maxWidth: "35px",
    fixedWidth: "35px",
    contentType: "Object",
    sortable: false,
    isRenderCustomComponent: true,
    applyWidth: false,
    textAlign: "right",
    colgroupstyle: "width:4%"
  }),

  [columnNames.CREATED_BY]: new ColumnMetaData({
    name: columnNames.CREATED_BY,
    heading: "Created By",
    contentClasses: [],
    contentType: "Date",
    sortable: true,
    sort: "ASC",
    applyWidth: true,
    showTooltip: true,
    colgroupstyle: "width:7%;"
  }),

  [columnNames.PREVIEW]: new ColumnMetaData({
    name: columnNames.PREVIEW,
    heading: "",
    contentClasses: [],
    maxWidth: "35px",
    fixedWidth: "35px",
    contentType: "Object",
    isRenderCustomComponent: true,
    applyWidth: true,
    colgroupstyle: "width:3%;"
  }),

  [columnNames.EMPTY_PIN]: new ColumnMetaData({
    name: columnNames.EMPTY_PIN,
    heading: "",
    contentClasses: [],
    maxWidth: "40px",
    fixedWidth: "40px",
    contentType: "Empty",
    isRenderCustomComponent: true,
    sortable: false,
    applyWidth: true,
    colgroupstyle: "width:3%;"
  }),

  [columnNames.EMPTY_SPACING]: new ColumnMetaData({
    name: columnNames.EMPTY_SPACING,
    heading: "",
    contentClasses: [],
    maxWidth: "1px",
    colgroupstyle: "width:4%;"
  }),

  [columnNames.ABBREVIATION]: new ColumnMetaData({
    name: columnNames.ABBREVIATION,
    heading: "Abbreviation",
    contentClasses: [],
    contentType: "String",
    sortable: true,
    sort: "ASC",
    applyWidth: true,
    showTooltip: true,
    colgroupstyle: "width:7%"
  })
};

export default columns;
